
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useAdmin();

  // 如果用戶已登入且是管理員，直接導向後台
  useEffect(() => {
    if (!authLoading && !adminLoading && user && isAdmin) {
      console.log('AdminLogin: User is already admin, redirecting to dashboard');
      navigate('/jay/dashboard');
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      console.log('AdminLogin: Attempting login');
      
      // 先進行身份驗證
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        console.error('AdminLogin: Auth error:', authError);
        throw authError;
      }

      if (!authData.user) {
        throw new Error('登入失敗');
      }

      console.log('AdminLogin: Auth successful, checking admin status');

      // 檢查是否為管理員
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', authData.user.id)
        .single();

      if (profileError) {
        console.error('AdminLogin: Profile error:', profileError);
        throw new Error('無法驗證管理員權限');
      }

      if (!profileData?.is_admin) {
        console.log('AdminLogin: User is not admin');
        // 登出用戶
        await supabase.auth.signOut();
        throw new Error('您沒有管理員權限');
      }

      console.log('AdminLogin: Admin verified, redirecting to dashboard');
      
      toast({
        title: '登入成功',
        description: '歡迎進入後台管理系統',
      });

      // 導向後台儀表板
      navigate('/jay/dashboard');

    } catch (error: any) {
      console.error('AdminLogin: Login error:', error);
      setError(error.message || '登入失敗，請重試');
      toast({
        variant: 'destructive',
        title: '登入失敗',
        description: error.message || '登入失敗，請重試',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 如果正在載入，顯示載入狀態
  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center font-bold">
            TruStay 後台管理
          </CardTitle>
          <p className="text-sm text-gray-600 text-center">
            請使用管理員帳號登入
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="電子郵件"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
              <Input
                type="password"
                placeholder="密碼"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={isLoading}
            >
              {isLoading ? '登入中...' : '登入後台'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button
              variant="link"
              onClick={() => navigate('/')}
              className="text-sm text-gray-600"
            >
              返回首頁
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
